/**
 * One LocalizationProvider for every MUI X date/time picker of the admin, in the admin's language.
 *
 * Without it the pickers spoke English whatever the language: the dish form's time picker showed
 * « SELECT TIME / CANCEL / OK » on a French screen (video shoot 03/11). MUI ships the texts for
 * fr/de/es/it/ru; a language it does not ship (Arabic, in MUI X v6) gets the visible texts —
 * toolbar titles and action buttons — from our own catalogue instead.
 * Dates are formatted with the matching dayjs locale (Arabic: 'ar-tn', Latin digits like the rest
 * of the admin — see utils/locale.js).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { enUS, frFR, deDE, esES, itIT, ruRU } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/it';
import 'dayjs/locale/ru';
import 'dayjs/locale/ar-tn';
import 'dayjs/locale/en-gb';

const MUI_LOCALE_TEXT = { en: enUS, fr: frFR, de: deDE, es: esES, it: itIT, ru: ruRU };
const DAYJS_LOCALE = { en: 'en-gb', fr: 'fr', de: 'de', es: 'es', it: 'it', ru: 'ru', ar: 'ar-tn' };

const textsOf = (pack) => pack.components.MuiLocalizationProvider.defaultProps.localeText;

/** The pickers' localeText for an i18next language ('fr', 'fr-FR', 'ar'…). */
export function pickersLocaleText(language, t) {
  const lng = String(language || 'en').slice(0, 2).toLowerCase();
  const pack = MUI_LOCALE_TEXT[lng];
  if (pack) return textsOf(pack);
  return {
    ...textsOf(enUS),
    cancelButtonLabel: t('Cancel'),
    clearButtonLabel: t('Clear'),
    okButtonLabel: t('OK'),
    todayButtonLabel: t('Today'),
    datePickerToolbarTitle: t('Select date'),
    dateTimePickerToolbarTitle: t('Select date & time'),
    timePickerToolbarTitle: t('Select time'),
  };
}

export function PickersLocalizationProvider({ children }) {
  const { t, i18n } = useTranslation();
  const language = i18n?.language;
  const lng = String(language || 'en').slice(0, 2).toLowerCase();
  const localeText = useMemo(() => pickersLocaleText(lng, t), [lng, t]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={DAYJS_LOCALE[lng] || 'en-gb'} localeText={localeText}>
      {children}
    </LocalizationProvider>
  );
}

export default PickersLocalizationProvider;
